import { hoehenDifferenzM, median, STANDARD_TEMPERATUR_C } from './hoehe';

/**
 * Geschoss aus Luftdruck (docs/maengel-verortung-plan.md 9.1–9.3, Paket P2).
 *
 * Einmal je Projekt stehen die Geschosshöhen in Metern relativ zu ±0,00 fest
 * (aus dem Schnitt oder vor Ort abgelaufen). Je Begehung gibt es einen
 * Nullpunkt: an bekannter Höhe gemessener Druck. Jede Bestätigung eines Orts
 * setzt ihn neu — damit spielt die Wetterdrift keine Rolle, solange zwischen
 * zwei Bestätigungen nicht zu viel Zeit vergeht. Das Alter des Nullpunkts geht
 * deshalb als Drift in Unsicherheit und Sicherheit ein.
 *
 * Reine Funktionen, nichts wirft.
 */

/** Ein Geschoss mit Höhe relativ ±0,00 (OKFF EG) in m. */
export interface Geschoss {
    id: string;
    name: string;
    hoeheM: number;
}

/** An dieser Höhe (m relativ ±0,00) wurde dieser Druck gemessen, Zeitpunkt Unix-ms. */
export interface Nullpunkt {
    hoeheM: number;
    druckHpa: number;
    t: number;
}

/**
 * Angenommene Wetterdrift des Luftdrucks in Höhe ausgedrückt. Ein normaler Tag
 * bringt 0,1–0,5 hPa/h (1–4 m/h); 2,5 m/h liegt bewusst im oberen Mittel.
 */
export const DRIFT_M_PRO_STUNDE = 2.5;

/**
 * Ergebnis der Schätzung.
 * - `geschoss`: das nächstgelegene Geschoss (bzw. per Hysterese gehaltene);
 *   `null` nur ohne Geschosse oder ohne gültige Höhe.
 * - `zwischen`: [unteres, oberes], wenn die Höhe deutlich zwischen zwei
 *   Geschossen liegt (Treppenhaus) — dann ist `sicherheit` 0.
 * - `abstandM`: Abstand der Höhe zu `geschoss`.
 * - `unsicherheitM`: 0,5 m Sensorrauschen + Drift seit dem Nullpunkt.
 * - `sicherheit`: 0–1.
 */
export interface GeschossSchaetzung {
    hoeheM: number;
    geschoss: Geschoss | null;
    zwischen: [Geschoss, Geschoss] | null;
    abstandM: number;
    unsicherheitM: number;
    sicherheit: number;
    nullpunktAlterMin: number;
}

/** Grundrauschen des Barometers relativ (≈ ±0,05 hPa) in m. */
const RAUSCHEN_M = 0.5;
/** Ein einzelnes Geschoss ohne Nachbarn: angenommene halbe Geschosshöhe. */
const HALBE_LUECKE_ALLEIN_M = 1.5;
/** Ab diesem Anteil der Geschosshöhe gilt eine Höhe als „zwischen“. */
const ZWISCHEN_ANTEIL = 0.35;
/** Zuschlag der Hysterese über die halbe Lücke hinaus. */
const HYSTERESE_M = 0.4;
/** Höhen, die näher beieinander liegen, zählen als gleich. */
const GLEICH_M = 1e-6;

function clamp01(x: number): number {
    if (!Number.isFinite(x)) return 0;
    return x < 0 ? 0 : x > 1 ? 1 : x;
}

/**
 * Halbe Distanz zum Nachbarn in Richtung der Abweichung: liegt die Höhe über
 * dem Geschoss, der obere Nachbar, sonst der untere; fehlt dieser, der andere;
 * gibt es keinen (nur ein Geschoss bzw. alle gleich hoch) → 1,5 m.
 */
function halbeLueckeZu(sortiert: readonly Geschoss[], index: number, hoeheM: number): number {
    const h = sortiert[index].hoeheM;
    let oben: number | null = null;
    for (let i = index + 1; i < sortiert.length; i++) {
        if (sortiert[i].hoeheM > h + GLEICH_M) {
            oben = sortiert[i].hoeheM - h;
            break;
        }
    }
    let unten: number | null = null;
    for (let i = index - 1; i >= 0; i--) {
        if (sortiert[i].hoeheM < h - GLEICH_M) {
            unten = h - sortiert[i].hoeheM;
            break;
        }
    }
    const bevorzugt = hoeheM > h ? oben : unten;
    const andere = hoeheM > h ? unten : oben;
    const luecke = bevorzugt ?? andere;
    return luecke === null ? HALBE_LUECKE_ALLEIN_M : luecke / 2;
}

/** Die beiden Geschosse, zwischen denen die Höhe liegt; `null` darunter/darüber. */
function klammer(sortiert: readonly Geschoss[], hoeheM: number): [Geschoss, Geschoss] | null {
    let unteres: Geschoss | null = null;
    let oberes: Geschoss | null = null;
    for (const g of sortiert) {
        if (g.hoeheM <= hoeheM) unteres = g;
        else if (oberes === null) oberes = g;
    }
    return unteres && oberes ? [unteres, oberes] : null;
}

/**
 * Geschoss aus dem aktuellen Druck schätzen (Regeln Plan 9.3, deterministisch):
 * - hoeheM = nullpunkt.hoeheM + hoehenDifferenzM(nullpunkt.druckHpa, druckHpa, T).
 * - nächstes Geschoss N; halbeLuecke siehe `halbeLueckeZu`.
 * - drift = DRIFT_M_PRO_STUNDE · Alter des Nullpunkts in h; unsicherheitM = 0,5 + drift.
 * - zwischen = [unteres, oberes], wenn abstandM > 0,35 · (2 · halbeLuecke) und
 *   die Höhe zwischen zwei Geschossen liegt.
 * - sicherheit = zwischen ? 0 : clamp01((halbeLuecke − abstandM) / halbeLuecke)
 *   · clamp01(1 − drift / halbeLuecke).
 * - Hysterese: `vorherigesId` gesetzt und |hoeheM − vorheriges.hoeheM| <
 *   halbeLuecke(vorheriges) + 0,4 → das vorherige Geschoss bleibt; abstandM,
 *   zwischen und sicherheit beziehen sich dann darauf.
 * Über dem obersten Geschoss (Gerüst, Dach) und unter dem untersten (Baugrube)
 * gibt es kein „zwischen“: dann das Randgeschoss mit sinkender Sicherheit.
 */
export function geschossSchaetzen(
    geschosse: readonly Geschoss[],
    nullpunkt: Nullpunkt,
    druckHpa: number,
    jetzt: number,
    opts?: { vorherigesId?: string | null; temperaturC?: number },
): GeschossSchaetzung {
    const temperaturC = opts?.temperaturC ?? STANDARD_TEMPERATUR_C;
    const hoeheM = nullpunkt.hoeheM + hoehenDifferenzM(nullpunkt.druckHpa, druckHpa, temperaturC);

    const alterMs = Number.isFinite(jetzt - nullpunkt.t) ? Math.max(0, jetzt - nullpunkt.t) : 0;
    const drift = DRIFT_M_PRO_STUNDE * (alterMs / 3_600_000);
    const unsicherheitM = RAUSCHEN_M + drift;
    const nullpunktAlterMin = alterMs / 60_000;

    const sortiert = geschosse
        .filter((g) => !!g && Number.isFinite(g.hoeheM))
        .slice()
        .sort((x, y) => x.hoeheM - y.hoeheM);

    if (sortiert.length === 0 || !Number.isFinite(hoeheM)) {
        return { hoeheM, geschoss: null, zwischen: null, abstandM: 0, unsicherheitM, sicherheit: 0, nullpunktAlterMin };
    }

    // Nächstes Geschoss; bei Gleichstand das untere (erste in Sortierung).
    let index = 0;
    for (let i = 1; i < sortiert.length; i++) {
        if (Math.abs(sortiert[i].hoeheM - hoeheM) < Math.abs(sortiert[index].hoeheM - hoeheM)) index = i;
    }

    // Hysterese: das vorherige Geschoss halten, solange die Höhe knapp daneben liegt.
    const vorherigesId = opts?.vorherigesId;
    if (vorherigesId) {
        const vi = sortiert.findIndex((g) => g.id === vorherigesId);
        if (vi >= 0 && vi !== index) {
            const grenze = halbeLueckeZu(sortiert, vi, hoeheM) + HYSTERESE_M;
            if (Math.abs(hoeheM - sortiert[vi].hoeheM) < grenze) index = vi;
        }
    }

    const geschoss = sortiert[index];
    const abstandM = Math.abs(hoeheM - geschoss.hoeheM);
    const halbeLuecke = halbeLueckeZu(sortiert, index, hoeheM);
    const zwischen = abstandM > ZWISCHEN_ANTEIL * 2 * halbeLuecke ? klammer(sortiert, hoeheM) : null;
    const sicherheit = zwischen
        ? 0
        : clamp01((halbeLuecke - abstandM) / halbeLuecke) * clamp01(1 - drift / halbeLuecke);

    return { hoeheM, geschoss, zwischen, abstandM, unsicherheitM, sicherheit, nullpunktAlterMin };
}

/** Bestätigter Ort „ich bin in diesem Geschoss“ → neuer Nullpunkt der Begehung. */
export function nullpunktAusGeschoss(geschoss: Geschoss, druckHpa: number, t: number): Nullpunkt {
    return { hoeheM: geschoss.hoeheM, druckHpa, t };
}

/** Vor Ort gesetzte Marke „hier ist das 2. OG“ mit der daraus gemessenen Höhe. */
export interface Messmarke {
    geschossName: string;
    hoeheM: number;
}

/** Vergleichsform eines Geschossnamens: getrimmt, Kleinbuchstaben, einfache Leerzeichen. */
export function geschossNameNormalisieren(name: string): string {
    return (typeof name === 'string' ? name : '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Geschosshöhen aus Messmarken: Median je (getrimmtem, case-insensitivem)
 * Namen, sortiert nach Höhe, id = normalisierter Name. Angezeigt wird der Name
 * der ersten Marke der Gruppe. Marken ohne Namen oder ohne gültige Höhe fallen weg.
 */
export function geschosseAusMarken(marken: readonly Messmarke[]): Geschoss[] {
    const gruppen = new Map<string, { name: string; hoehen: number[] }>();
    for (const m of marken) {
        if (!m || !Number.isFinite(m.hoeheM)) continue;
        const id = geschossNameNormalisieren(m.geschossName);
        if (!id) continue;
        const gruppe = gruppen.get(id);
        if (gruppe) gruppe.hoehen.push(m.hoeheM);
        else gruppen.set(id, { name: m.geschossName.trim().replace(/\s+/g, ' '), hoehen: [m.hoeheM] });
    }
    return [...gruppen.entries()]
        .map(([id, g]) => ({ id, name: g.name, hoeheM: median(g.hoehen) }))
        .sort((x, y) => x.hoeheM - y.hoeheM);
}

// Name, Trenner (Leerzeichen, „:“ oder „=“), Höhe mit Vorzeichen (±, +, -, −)
// und Komma oder Punkt, optional „m“. Der Name ist genügsam (lazy), damit
// „1.OG 3,05“ als Name „1.OG“ und Höhe 3,05 zerfällt.
const ZEILE_MUSTER = /^(.*?)[\s:=]+([±+\-−]?)\s*(\d+(?:[.,]\d+)?)\s*m?$/i;
const NUR_ZAHL_MUSTER = /^[±+\-−]?\s*\d+(?:[.,]\d+)?\s*m?$/i;

/**
 * Freitext „EG 0; 1.OG 3,05; UG -3,2“ (Zeilen oder „;“ getrennt, Komma oder
 * Punkt als Dezimalzeichen, ±/+ erlaubt, „m“ dahinter erlaubt) → Geschosse
 * sortiert nach Höhe; Fehler je Zeile als Klartext. Doppelte Namen: die erste
 * Zeile gilt, jede weitere ist ein Fehler.
 */
export function geschosseLesen(text: string): { geschosse: Geschoss[]; fehler: string[] } {
    const geschosse: Geschoss[] = [];
    const fehler: string[] = [];
    const teile = (typeof text === 'string' ? text : '').split(/[\n;]/);
    teile.forEach((roh, i) => {
        const zeile = roh.replace(/[\u00a0\u202f\u2009]/g, ' ').trim();
        if (!zeile) return;
        const nr = i + 1;
        const treffer = ZEILE_MUSTER.exec(zeile);
        if (!treffer && NUR_ZAHL_MUSTER.test(zeile)) {
            fehler.push(`Eintrag ${nr} „${zeile}“: Name des Geschosses fehlt.`);
            return;
        }
        if (!treffer) {
            fehler.push(`Eintrag ${nr} „${zeile}“: keine Höhe erkannt — erwartet z. B. „1.OG 3,05“.`);
            return;
        }
        const name = treffer[1].trim().replace(/[:=]+$/, '').trim().replace(/\s+/g, ' ');
        if (!name) {
            fehler.push(`Eintrag ${nr} „${zeile}“: Name des Geschosses fehlt.`);
            return;
        }
        const betrag = Number(treffer[3].replace(',', '.'));
        const hoeheM = treffer[2] === '-' || treffer[2] === '−' ? -betrag : betrag;
        const id = geschossNameNormalisieren(name);
        if (geschosse.some((g) => g.id === id)) {
            fehler.push(`Eintrag ${nr} „${zeile}“: Geschoss „${name}“ ist doppelt — nur der erste Eintrag gilt.`);
            return;
        }
        // −0 als 0 ablegen, sonst zeigt die Anzeige „-0“.
        geschosse.push({ id, name, hoeheM: hoeheM === 0 ? 0 : hoeheM });
    });
    geschosse.sort((x, y) => x.hoeheM - y.hoeheM);
    return { geschosse, fehler };
}
